// Marketplace actions: buy a listing, add a purchase to a project, sell a flow, share work to the Gallery.
import { html, raw, mount, toast } from '../lib/dom';
import { openModal } from './modal';
import { icon } from './icons';
import { assetThumb, coverThumb } from './thumb';
import { chainPreview, priceTag } from './community';
import { getCreator, purchaseListing, addPurchaseToProject, createListing, publishWork, flowSnapshot, listingForFlow, myListings, CATEGORIES, LICENSES, PLATFORM_FEE } from '../api/marketplace';
import { listProjects, currentUser, getProject } from '../api/workspace';
import { projectFlows, getFlow } from '../api/flows';
import { availableAssets, listAssets, ownerLabel } from '../api/library';
import { money } from '../lib/format';

const projectOptions = () => listProjects().map((p?: any) => html`<option value="${p.id}">${p.name}</option>`);
const isWork = (a?: any) => ['IMAGE', 'VIDEO', 'CHARACTER'].includes(a.type) && a.status === 'Approved' && (a.scope === 'PROJECT' || a.scope === 'BRAND');

export function openPurchase(l?: any, { onDone }: any = {}) {
  const c = getCreator(l.seller_id);
  openModal({
    title: l.price ? 'Checkout' : 'Get this flow',
    body: html`
      <div class="row" style="gap:14px">${coverThumb(l, 'thumb mini')}<div class="col" style="gap:3px;flex:1;min-width:0"><strong class="ellipsis" style="font-weight:500">${l.title}</strong><span class="muted" style="font-size:12.5px">by ${c.name} · v${l.version} · ${l.node_count} nodes</span></div>${priceTag(l.price)}</div>
      <dl class="kv">
        <dt>License</dt><dd><strong style="font-weight:500">${l.license}</strong> — ${LICENSES[l.license]}</dd>
        ${l.price ? html`<dt>Payment</dt><dd>A payment provider is not connected. This listing cannot be purchased yet.</dd><dt>Total</dt><dd class="mono">${money(l.price)}</dd>` : ''}
      </dl>
      <div class="field"><label>Add a copy to project</label>
        <select class="select" data-project><option value="">Assets only — decide later</option>${projectOptions()}</select>
        <span class="hint">You get an editable copy. It’s also saved to Assets as a template, so any project can use it later.</span></div>
      ${l.requirements.length ? html`<div class="banner info">${icon('plug')}<div>Needs ${l.requirements.join(', ')}. The seller’s keys are never included — the flow runs on your own connections.</div></div>` : ''}
      <label class="row" style="font-size:13px;gap:8px;cursor:pointer"><input type="checkbox" data-agree> I accept the ${l.license} license terms</label>`,
    foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn primary" data-confirm disabled>${l.price ? 'Payment unavailable' : 'Get for free'}</button>`,
    onMount(m?: any, close?: any) {
      const btn = m.querySelector('[data-confirm]');
      m.querySelector('[data-agree]').addEventListener('change', (e?: any) => { btn.disabled = Boolean(l.price) || !e.target.checked; });
      btn.addEventListener('click', () => {
        const projectId = m.querySelector('[data-project]').value || null;
        const r = purchaseListing(l.id, { projectId, userId: currentUser().id });
        close();
        toast(r.flowId ? `“${l.title}” added to ${getProject(projectId).name}` : `“${l.title}” saved to Assets`);
        if (onDone) onDone(r);
        else location.hash = r.flowId ? `/projects/${projectId}/flows/${r.flowId}` : `/assets/${r.assetId}`;
      });
    },
  });
}

export function openAddToProject(purchase?: any) {
  openModal({
    title: `Add “${purchase.listing.title}” to a project`,
    body: html`<div class="field"><label>Project</label><select class="select" data-project autofocus>${projectOptions()}</select><span class="hint">Creates a new, editable draft flow from your purchased template.</span></div>`,
    foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn primary" data-confirm>Add flow</button>`,
    onMount(m?: any, close?: any) {
      m.querySelector('[data-confirm]').addEventListener('click', () => {
        const projectId = m.querySelector('[data-project]').value;
        const flowId = addPurchaseToProject(purchase.id, projectId, currentUser().id);
        close();
        toast(`Added to ${getProject(projectId).name}`);
        location.hash = `/projects/${projectId}/flows/${flowId}`;
      });
    },
  });
}

export function openSellFlow({ flowId }: any = {}) {
  const groups = listProjects().map((p?: any) => [p, projectFlows(p.id)]).filter(([, flows]: any) => flows.length);
  const selected = flowId || groups[0][1][0].id;
  openModal({
    title: 'Sell a flow', wide: true,
    body: html`<div class="form" style="max-width:none">
      <div class="form-row">
        <div class="field"><label>Flow</label><select class="select" data-f="flow">${groups.map(([p, flows]: any) => html`<optgroup label="${p.name}">${flows.map((f?: any) => html`<option value="${f.id}" ${raw(f.id === selected ? 'selected' : '')}>${f.name}</option>`)}</optgroup>`)}</select></div>
        <div class="field"><label>Category</label><select class="select" data-f="category">${CATEGORIES.map((c?: any) => html`<option>${c}</option>`)}</select></div>
      </div>
      <div data-existing></div>
      <div class="field"><label>Title</label><input class="input" data-f="title" maxlength="80"></div>
      <div class="field"><label>Tagline</label><input class="input" data-f="tagline" maxlength="120" placeholder="One sentence: what it makes, and for whom"></div>
      <div class="field"><label>Description</label><textarea class="textarea" data-f="description" rows="3" placeholder="What goes in, what comes out, how to adapt it to a brand"></textarea></div>
      <div class="form-row">
        <div class="field"><label>Price, USD</label><input class="input" data-f="price" type="number" min="0" step="1" value="29"><span class="hint" data-payout></span></div>
        <div class="field"><label>License</label><select class="select" data-f="license">${Object.entries(LICENSES).map(([k, d]: any) => html`<option value="${k}" ${raw(k === 'Commercial' ? 'selected' : '')}>${k} — ${d}</option>`)}</select></div>
      </div>
      <div class="field"><label>What buyers get</label><div data-chain></div><span class="hint" data-snap></span></div>
      <div class="field"><label>Example outputs</label><span class="hint">Pick a few — they become the listing’s preview and are posted to the Gallery.</span><div class="grid auto-sm pick-grid" data-works></div></div>
      <div class="banner info">${icon('admin')}<div>Buyers get a copy of the graph and its settings. Your assets, runs and connection keys are never shared — required providers are listed so buyers connect their own. Listings go live after review.</div></div>
    </div>`,
    foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn primary" data-submit>${icon('tag')}Submit for review</button>`,
    onMount(m?: any, close?: any) {
      const f = (name?: any) => m.querySelector(`[data-f="${name}"]`);
      const picked = new Set();
      let titleTouched = false;
      const sync = () => {
        const flow = getFlow(f('flow').value);
        const snap = flowSnapshot(flow.id);
        const existing = listingForFlow(flow.id);
        if (!titleTouched) f('title').value = flow.name;
        mount(m.querySelector('[data-existing]'), existing ? html`<div class="banner info">${icon('tag')}<div>This flow is already listed as <a href="#/marketplace/${existing.id}" data-close style="color:var(--fern)">${existing.title}</a>. Submitting creates a separate listing.</div></div>` : '');
        mount(m.querySelector('[data-chain]'), chainPreview(snap.chain));
        m.querySelector('[data-snap]').textContent = `${snap.node_count} nodes · ${snap.models.join(', ') || 'no models'} · about ${money(snap.est_cost_per_run, 3)} per run on the buyer’s providers`;
        const works = availableAssets(flow.project_id).filter(isWork).slice(0, 12);
        picked.clear();
        mount(m.querySelector('[data-works]'), works.length
          ? works.map((a?: any) => html`<button type="button" class="card hover entity-card" data-pick="${a.id}" style="border:none;padding:0;text-align:left;cursor:pointer">${assetThumb(a)}<div class="body" style="padding:8px 10px"><span class="title ellipsis" style="font-size:12px">${a.name}</span><span class="meta">${a.type} · ${ownerLabel(a)}</span></div></button>`)
          : html`<div class="muted" style="font-size:13px">No approved outputs in this project yet — the listing will use a generated cover.</div>`);
      };
      const payout = () => {
        const p = Math.max(0, Math.round(Number(f('price').value) || 0));
        m.querySelector('[data-payout]').textContent = p ? `You receive ${money(p * (1 - PLATFORM_FEE))} per sale (${PLATFORM_FEE * 100}% platform fee)` : 'Free — counted as installs, no payout';
      };
      f('flow').addEventListener('change', sync);
      f('title').addEventListener('input', () => { titleTouched = true; });
      f('price').addEventListener('input', payout);
      m.querySelector('[data-works]').addEventListener('click', (e?: any) => {
        const b = e.target.closest('[data-pick]');
        if (!b) return;
        if (picked.has(b.dataset.pick)) picked.delete(b.dataset.pick); else picked.add(b.dataset.pick);
        b.classList.toggle('picked', picked.has(b.dataset.pick));
      });
      m.querySelector('[data-submit]').addEventListener('click', () => {
        const title = f('title').value.trim();
        if (!title) { f('title').focus(); return; }
        const flow = getFlow(f('flow').value);
        const id = createListing({
          flowId: flow.id, title, tagline: f('tagline').value.trim() || flow.description, description: f('description').value.trim(),
          category: f('category').value, price: f('price').value, license: f('license').value, workAssetIds: [...picked],
        });
        close();
        toast('Submitted for review — it goes live once approved');
        location.hash = `/marketplace/${id}`;
      });
      sync(); payout();
    },
  });
}

export function openShareWork() {
  const works = listAssets().filter(isWork).slice(0, 24);
  const listings = myListings().filter((l?: any) => l.status === 'published');
  let pickedId: string | null = null;
  openModal({
    title: 'Share your work', wide: true,
    body: html`
      <div class="field"><label>Pick an output from Assets</label><div class="grid auto-sm pick-grid" data-works>${works.map((a?: any) => html`<button type="button" class="card hover entity-card" data-pick="${a.id}" data-name="${a.name}" style="border:none;padding:0;text-align:left;cursor:pointer">${assetThumb(a)}<div class="body" style="padding:8px 10px"><span class="title ellipsis" style="font-size:12px">${a.name}</span><span class="meta">${ownerLabel(a)}</span></div></button>`)}</div></div>
      <div class="form-row">
        <div class="field"><label>Title</label><input class="input" data-title placeholder="Give it a name"></div>
        <div class="field"><label>Link a flow for sale</label><select class="select" data-listing><option value="">Don’t link</option>${listings.map((l?: any) => html`<option value="${l.id}">${l.title} — ${l.price ? money(l.price, 0) : 'Free'}</option>`)}</select><span class="hint">People can buy the flow straight from your post.</span></div>
      </div>`,
    foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn primary" data-publish disabled>Publish to Gallery</button>`,
    onMount(m?: any, close?: any) {
      const btn = m.querySelector('[data-publish]');
      m.querySelector('[data-works]').addEventListener('click', (e?: any) => {
        const b = e.target.closest('[data-pick]');
        if (!b) return;
        m.querySelectorAll('[data-pick].picked').forEach((x?: any) => x.classList.remove('picked'));
        b.classList.add('picked');
        pickedId = b.dataset.pick;
        const title = m.querySelector('[data-title]');
        if (!title.value) title.value = b.dataset.name;
        btn.disabled = false;
      });
      btn.addEventListener('click', () => {
        const id = publishWork({ assetId: pickedId, title: m.querySelector('[data-title]').value.trim(), listingId: m.querySelector('[data-listing]').value || null });
        close();
        toast('Posted to the Gallery');
        location.hash = `/gallery/${id}`;
      });
    },
  });
}
